import { useEffect, useState } from 'react';
import { fetchNotifications, markNotificationRead, deleteNotification, } from 'src/apis/notifications.api';
import { INBOX_STATES } from './config';
const pageSize = 10;
const initialNotificationPageData = {
    notifications: [],
    pageNumber: 1,
    isLastPage: true,
};
const notificationCache = {};
const getNotificationCache = (inboxState) => {
    return notificationCache[inboxState];
};
const setNotificationCache = (inboxState, data) => {
    notificationCache[inboxState] = data;
};
const clearCache = () => {
    Object.keys(notificationCache).forEach((key) => {
        delete notificationCache[key];
    });
};
// Trim response to return only what is required by the front end
const trimResponse = (response) => {
    return {
        notifications: response.data,
        pageNumber: response.pageNumber,
        isLastPage: response.lastPage,
    };
};
const removeNotificationById = (notifications, notificationUuid) => {
    return notifications.filter((notification) => notification.notificationUuid !== notificationUuid);
};
const useNotification = (merchantNumber, language) => {
    const [notificationPageData, setNotificationPageData] = useState(initialNotificationPageData);
    const [currentInboxState, setCurrentInboxState] = useState(INBOX_STATES.UNREAD);
    const [isLoading, setIsLoading] = useState(false);
    // Set state and cache
    function setPageData(selectedInboxState, newPageData) {
        setNotificationPageData(newPageData);
        setNotificationCache(selectedInboxState, newPageData);
    }
    const getFetchNotifications = async (pageNumber = 1, inboxState) => {
        const readStatus = inboxState === INBOX_STATES.READ;
        try {
            return await fetchNotifications(merchantNumber, pageNumber, pageSize, readStatus, language);
        }
        catch (error) {
            console.error('Error fetching notifications:', error);
        }
    };
    const handleNextPage = async () => {
        // fetch new notifications
        const response = await getFetchNotifications(notificationPageData.pageNumber + 1, currentInboxState);
        if (!response)
            return;
        // merge the new notifications with the existing notifications
        const mergedNotifications = [...notificationPageData.notifications, ...response.data];
        // trim and remap response data
        const notificationData = {
            notifications: mergedNotifications,
            pageNumber: response.pageNumber,
            isLastPage: response.lastPage,
        };
        // cache the trimmed response and set the state
        setNotificationCache(currentInboxState, notificationData);
        setNotificationPageData(notificationData);
    };
    const getNotifications = async (selectedInboxState) => {
        setIsLoading(true);
        const response = await getFetchNotifications(1, selectedInboxState);
        // if response is undefined/empty, set loading to false and break out of function;
        if (!response) {
            setNotificationPageData(initialNotificationPageData);
            setIsLoading(false);
            return;
        }
        const trimmedResponse = trimResponse(response);
        setPageData(selectedInboxState, trimmedResponse);
        setIsLoading(false);
    };
    async function markAllRead() {
        try {
            setIsLoading(true);
            // mark all as read
            await markNotificationRead(merchantNumber, undefined, true);
            // clear page cache so b/e can pull the update tabs
            delete notificationCache[INBOX_STATES.READ];
            delete notificationCache[INBOX_STATES.UNREAD];
            // since you can only mark all as read on the unread tab, we can just reset the state
            setNotificationPageData(initialNotificationPageData);
        }
        catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
        setIsLoading(false);
    }
    async function markAsRead(notificationUuid) {
        await markNotificationRead(merchantNumber, notificationUuid, false);
        const newNotifications = removeNotificationById(notifications, notificationUuid);
        const newPageData = { ...notificationPageData, notifications: newNotifications };
        // Clear READ cache section so that next time we pull the data from the b/e
        delete notificationCache[INBOX_STATES.READ];
        setPageData(currentInboxState, newPageData);
    }
    async function notificationDelete(notificationUuid) {
        await deleteNotification(merchantNumber, notificationUuid);
        const newNotifications = removeNotificationById(notifications, notificationUuid);
        const newPageData = { ...notificationPageData, notifications: newNotifications };
        setPageData(currentInboxState, newPageData);
    }
    const handleTabChange = (selectedInboxState) => {
        // Disabled tab changing when loading
        if (isLoading)
            return;
        // when the inbox state changes attempt to pull cached data
        const cachedNotifications = getNotificationCache(selectedInboxState);
        if (cachedNotifications) {
            setNotificationPageData(cachedNotifications);
        }
        else {
            getNotifications(selectedInboxState);
        }
        setCurrentInboxState(selectedInboxState);
    };
    useEffect(() => {
        getNotifications(currentInboxState);
        // Clear cache upon unmount
        return () => clearCache();
    }, []);
    const { notifications, pageNumber, isLastPage } = notificationPageData;
    return {
        isLoading,
        notifications,
        pageNumber,
        isLastPage,
        currentInboxState,
        handleNextPage,
        markAllRead,
        markAsRead,
        notificationDelete,
        deleteNotification,
        handleTabChange,
    };
};
export default useNotification;
